import { z } from "zod";
import {
	VIEWER_SPLIT_PANE_MAX_PERCENT,
	VIEWER_SPLIT_PANE_MIN_PERCENT,
} from "@/shared/lib/percent";

export const SETTINGS_STORE_VERSION = 1;

export const DEFAULT_PERSISTED_SETTINGS = {
	theme: "theme-default",
	freeRoam: false,
	showDualCalendar: false,
	showToolCalls: true,
	sendTypingIndicator: false,
	viewerEnabled: false,
	viewerSplitPaneLocked: false,
	viewerSplitPaneHeight: null,
	viewerSplitOrientation: "vertical",
	viewerToolbarHidden: true,
	editorToolbarSize: 100,
	calendarDensity: 100,
	calendarLocale: "gregorian",
	componentStyle: "default",
} as const;

const settingsPersistedStateShape = {
	theme: z.string(),
	freeRoam: z.boolean(),
	showDualCalendar: z.boolean(),
	showToolCalls: z.boolean(),
	sendTypingIndicator: z.boolean(),
	viewerEnabled: z.boolean(),
	viewerSplitPaneLocked: z.boolean(),
	viewerSplitPaneHeight: z
		.number()
		.min(VIEWER_SPLIT_PANE_MIN_PERCENT)
		.max(VIEWER_SPLIT_PANE_MAX_PERCENT)
		.nullable(),
	// "vertical" stacks the two panes (viewer on top); "horizontal" puts them
	// side by side.
	viewerSplitOrientation: z.enum(["vertical", "horizontal"]),
	viewerToolbarHidden: z.boolean(),
	editorToolbarSize: z.number().int().positive(),
	calendarDensity: z.number().int().min(80).max(500),
	calendarLocale: z.enum(["gregorian", "hijri"]),
	componentStyle: z.enum(["t0ggle", "default"]),
};

const settingsPersistedStateSchema = z.object(settingsPersistedStateShape);

const readableSettingsPersistedStateSchema = z.object({
	theme: settingsPersistedStateShape.theme.default(
		DEFAULT_PERSISTED_SETTINGS.theme,
	),
	freeRoam: settingsPersistedStateShape.freeRoam.default(
		DEFAULT_PERSISTED_SETTINGS.freeRoam,
	),
	showDualCalendar: settingsPersistedStateShape.showDualCalendar.default(
		DEFAULT_PERSISTED_SETTINGS.showDualCalendar,
	),
	showToolCalls: settingsPersistedStateShape.showToolCalls.default(
		DEFAULT_PERSISTED_SETTINGS.showToolCalls,
	),
	sendTypingIndicator: settingsPersistedStateShape.sendTypingIndicator.default(
		DEFAULT_PERSISTED_SETTINGS.sendTypingIndicator,
	),
	viewerEnabled: settingsPersistedStateShape.viewerEnabled.default(
		DEFAULT_PERSISTED_SETTINGS.viewerEnabled,
	),
	viewerSplitPaneLocked:
		settingsPersistedStateShape.viewerSplitPaneLocked.default(
			DEFAULT_PERSISTED_SETTINGS.viewerSplitPaneLocked,
		),
	viewerSplitPaneHeight:
		settingsPersistedStateShape.viewerSplitPaneHeight.default(
			DEFAULT_PERSISTED_SETTINGS.viewerSplitPaneHeight,
		),
	viewerSplitOrientation:
		settingsPersistedStateShape.viewerSplitOrientation.default(
			DEFAULT_PERSISTED_SETTINGS.viewerSplitOrientation,
		),
	viewerToolbarHidden: settingsPersistedStateShape.viewerToolbarHidden.default(
		DEFAULT_PERSISTED_SETTINGS.viewerToolbarHidden,
	),
	editorToolbarSize: settingsPersistedStateShape.editorToolbarSize.default(
		DEFAULT_PERSISTED_SETTINGS.editorToolbarSize,
	),
	calendarDensity: settingsPersistedStateShape.calendarDensity.default(
		DEFAULT_PERSISTED_SETTINGS.calendarDensity,
	),
	calendarLocale: settingsPersistedStateShape.calendarLocale.default(
		DEFAULT_PERSISTED_SETTINGS.calendarLocale,
	),
	componentStyle: settingsPersistedStateShape.componentStyle.default(
		DEFAULT_PERSISTED_SETTINGS.componentStyle,
	),
});

const writableSettingsStorageValueSchema = z.object({
	state: settingsPersistedStateSchema,
	version: z.number().int().nonnegative().optional(),
});

const readableSettingsStorageValueSchema = z.object({
	state: readableSettingsPersistedStateSchema,
	version: z.number().int().nonnegative().default(SETTINGS_STORE_VERSION),
});

/**
 * Validate and canonicalize a stored settings envelope before Zustand sees it.
 * Older envelopes retain every valid preference and receive defaults only for
 * fields introduced after they were written. The normalized JSON is then what
 * Zustand hydrates and writes on the next settings change.
 */
export function normalizeStoredSettingsValue(raw: string): string | null {
	try {
		const parsed = JSON.parse(raw) as unknown;
		const result = readableSettingsStorageValueSchema.safeParse(parsed);
		return result.success ? JSON.stringify(result.data) : null;
	} catch {
		return null;
	}
}

/** Keep writes strict so application bugs cannot persist incomplete state. */
export function isWritableSettingsStorageValue(value: unknown): boolean {
	return writableSettingsStorageValueSchema.safeParse(value).success;
}
